import React, { Ref } from 'react';
import {
  FormikErrors,
  FormikHelpers,
  FormikProps,
  FormikState,
  FormikTouched,
  FormikValues,
} from 'formik';

import { Field, FieldOverrides } from './Field';
import { Layout, SectionLayout } from './Section';

export type Fields<TValues extends FormikValues = FormikValues> = {
  [TKey in keyof TValues]?: Field<TValues[TKey], TValues>;
} & {
  [key: string]: Field<any, any>;
};

export enum VisibilityOperators {
  Equal = 'EQUAL',
  NotEqual = 'NOT_EQUAL',
  In = 'IN',
}

export interface FieldVisibility {
  value: any;
  fieldKey: string;
  operator: VisibilityOperators;
}

export interface AutoSaveProps {
  debounceMs?: number;
}

export type FormRefType<T> = Ref<FormikProps<T>>;

export interface FormProps<TValues extends FormikValues = FormikValues> extends AutoSaveProps {
  formName: string;
  validate?: (
    values: TValues,
  ) => void | Promise<FormikErrors<TValues>> | Partial<Record<keyof TValues, JSX.Element | string>>; // handle nested errors in 3rd member
  fields: Fields<TValues>;
  layout?: Layout;
  sectionLayout?: SectionLayout;
  initialValues: TValues;
  onChange?: Function; // add type
  onSubmit: (values: TValues, formikHelpers: FormikHelpers<TValues>) => void | Promise<any>;
  setSubmitRef?: any; // add type
  enableReinitialize?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  validateOnMount?: boolean;
  autoSave?: boolean;
  focusErrorOnSubmit?: boolean;
  FormField?: React.FC<FormElementProps>;
  overrides?: FieldOverrides;
  formRef?: FormRefType<TValues>;
  validateNudgeState?: (values: TValues, nudgeStates: any) => any | Promise<any>;
  children?: React.ReactNode;
}

export enum FormElementType {
  FormField = 'FORM_FIELD',
  FormSection = 'FORM_SECTION',
}

export interface FormElementProps<F extends Field = Field> {
  formik: any;
  field: F;
  className?: string;
  containerClassName?: string;
  type: FormElementType;
  overrides?: FieldOverrides;
}

export type FieldError = string | undefined | React.ReactNode | { [key: string]: string }; // define key type. should value type be string | undefined | ReactNode?

export type ScrollBlockPosition = 'center' | 'end' | 'nearest' | 'start';
export type SetFieldValue<TValues extends FormikValues = FormikValues> =
  FormikHelpers<TValues>['setFieldValue'];

export * from './actions';
export * from './Field';
export * from './Section';
export * from './ValidationSchema';
export type { FormikContextType, FormikHelpers } from 'formik';
